import { type ReactNode } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useBrand } from "@branding/useBrand";

/**
 * Public marketing chrome (the front door served at `/`). Header + footer wrap
 * every marketing page via <Outlet/>. All product/sign-in CTAs cross into the
 * REAL app router (mounted under basename="/app") through plain <a href="/app">,
 * which does a full navigation into the product's existing auth gate — there is
 * no separate marketing-side auth, so no parallel credential path to attack.
 *
 * White-label: brand name / mark / legal footer all derive from brand.config,
 * so re-skinning the product re-skins this site too.
 */

/** Entry point into the product. `/app` resolves to the setup wizard (new org),
 *  the sign-in gate (returning user), or the app itself — the real auth flow. */
export const APP_URL = "/app";

export function AppCta({
  children,
  className = "mk-btn primary",
}: {
  children: ReactNode;
  className?: string;
}) {
  // Plain anchor (not <Link>): the app lives in a different router/basename, so
  // a hard navigation is required to hand off to the product bundle.
  return (
    <a className={className} href={APP_URL}>
      {children}
    </a>
  );
}

const NAV = [
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
];

export function MarketingLayout() {
  const brand = useBrand();
  const initial = (brand.name.trim()[0] || "L").toUpperCase();

  return (
    <div className="mk">
      <a className="mk-skip" href="#mk-main">
        Skip to content
      </a>

      <header className="mk-top">
        <Link to="/" className="mk-brand" aria-label={`${brand.name} home`}>
          <span className="mk-brand-mark" aria-hidden="true">
            {initial}
          </span>
          <span className="mk-brand-name">{brand.name}</span>
        </Link>

        <nav className="mk-nav" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className="mk-link">
              {n.label}
            </NavLink>
          ))}
          <span className="mk-nav-cta">
            <a className="mk-btn secondary sm" href={APP_URL}>
              Sign in
            </a>
            <AppCta className="mk-btn primary sm">Start free trial</AppCta>
          </span>
        </nav>
      </header>

      <main id="mk-main" className="mk-main">
        <Outlet />
      </main>

      <footer className="mk-foot">
        <div className="mk-foot-inner">
          <div className="col">
            <Link to="/" className="mk-brand" aria-label={`${brand.name} home`}>
              <span className="mk-brand-mark" aria-hidden="true">
                {initial}
              </span>
              <span className="mk-brand-name">{brand.name}</span>
            </Link>
            <p className="legal" style={{ marginTop: "var(--s3)" }}>
              {brand.tagline}
            </p>
          </div>
          <div className="col">
            <h4>Product</h4>
            <Link to="/features">Features</Link>
            <Link to="/pricing">Pricing</Link>
            <a href={APP_URL}>Sign in</a>
            <a href={APP_URL}>Start free trial</a>
          </div>
          <div className="col">
            <h4>Company &amp; legal</h4>
            <Link to="/about">About</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
        </div>
        <div className="mk-foot-bottom">
          {brand.legalFooter} Sample pricing shown for illustration; no payment is
          collected on this site.
        </div>
      </footer>
    </div>
  );
}
